import React, { useEffect, useMemo, useState } from "react";
import PageHeader from "@/components/PageHeader";
import api, { formatApiError } from "@/api/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { fmtDate, fromNow } from "@/lib/format";
import {
    CheckCircle2, XCircle, PauseCircle, PlayCircle, KeyRound, Building2,
} from "lucide-react";

const STATUS_STYLE = {
    pending_approval: "bg-amber-50 text-amber-800 border-amber-200",
    active: "bg-emerald-50 text-emerald-700 border-emerald-200",
    suspended: "bg-slate-100 text-slate-500 border-slate-300",
    rejected: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function PlatformFirms() {
    const [firms, setFirms] = useState([]);
    const [filter, setFilter] = useState("pending_approval");
    const [plans, setPlans] = useState({});
    const [rejectFirm, setRejectFirm] = useState(null);
    const [reason, setReason] = useState("");
    const [planFirm, setPlanFirm] = useState(null);
    const [planSel, setPlanSel] = useState("trial");
    const [pwFirm, setPwFirm] = useState(null);
    const [newPw, setNewPw] = useState("");

    const load = () =>
        api
            .get("/platform/firms", { params: filter === "all" ? {} : { status: filter } })
            .then((r) => setFirms(r.data));

    useEffect(() => {
        load();
        api.get("/platform/plans").then((r) => setPlans(r.data));
        // eslint-disable-next-line
    }, [filter]);

    const approve = async (f) => {
        await api.post(`/platform/firms/${f.id}/approve`);
        toast.success(`${f.name} approved`);
        load();
    };
    const suspend = async (f) => {
        if (!window.confirm(`Suspend ${f.name}? All logins will be blocked.`)) return;
        await api.post(`/platform/firms/${f.id}/suspend`);
        toast.success("Suspended");
        load();
    };
    const activate = async (f) => {
        await api.post(`/platform/firms/${f.id}/activate`);
        toast.success("Activated");
        load();
    };
    const doReject = async () => {
        try {
            await api.post(`/platform/firms/${rejectFirm.id}/reject`, { reason });
            toast.success("Rejected");
            setRejectFirm(null);
            setReason("");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };
    const savePlan = async () => {
        try {
            await api.patch(`/platform/firms/${planFirm.id}/plan`, { plan: planSel });
            toast.success("Plan updated");
            setPlanFirm(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };
    const doResetPw = async () => {
        try {
            await api.post(`/platform/firms/${pwFirm.id}/reset-password`, { new_password: newPw });
            toast.success("Password reset — share it securely with the Firm Admin");
            setPwFirm(null);
            setNewPw("");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const counts = useMemo(() => {
        const c = { pending_approval: 0, active: 0, suspended: 0, rejected: 0 };
        firms.forEach((f) => (c[f.status] = (c[f.status] || 0) + 1));
        return c;
    }, [firms]);

    return (
        <div className="space-y-6" data-testid="platform-firms-page">
            <PageHeader
                title="Firms"
                subtitle="Approve new tenants, manage subscriptions and lifecycle. No firm data is visible here."
            />

            <Tabs value={filter} onValueChange={setFilter}>
                <TabsList>
                    <TabsTrigger value="pending_approval" data-testid="tab-pending">
                        Pending {counts.pending_approval ? `(${counts.pending_approval})` : ""}
                    </TabsTrigger>
                    <TabsTrigger value="active">Active</TabsTrigger>
                    <TabsTrigger value="suspended">Suspended</TabsTrigger>
                    <TabsTrigger value="rejected">Rejected</TabsTrigger>
                    <TabsTrigger value="all">All</TabsTrigger>
                </TabsList>
            </Tabs>

            <Card className="overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                            <tr>
                                <th className="text-left px-4 py-3 font-semibold">Firm</th>
                                <th className="text-left px-4 py-3 font-semibold">Status</th>
                                <th className="text-left px-4 py-3 font-semibold">Plan</th>
                                <th className="text-right px-4 py-3 font-semibold">Users</th>
                                <th className="text-right px-4 py-3 font-semibold">Clients</th>
                                <th className="text-right px-4 py-3 font-semibold">Storage</th>
                                <th className="text-left px-4 py-3 font-semibold">Registered</th>
                                <th className="px-4 py-3" />
                            </tr>
                        </thead>
                        <tbody>
                            {firms.map((f) => (
                                <tr key={f.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`firm-row-${f.id}`}>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            <Building2 size={15} className="text-primary shrink-0" />
                                            <div>
                                                <div className="font-medium text-slate-800">{f.name}</div>
                                                <div className="text-xs text-slate-500">{f.email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`badge-status ${STATUS_STYLE[f.status] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
                                            {(f.status || "").replace("_", " ")}
                                        </span>
                                        {f.status === "rejected" && f.reject_reason && (
                                            <div className="text-xs text-rose-600 mt-1 max-w-xs truncate" title={f.reject_reason}>
                                                {f.reject_reason}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-slate-700 uppercase text-xs font-mono">{f.plan}</td>
                                    <td className="px-4 py-3 text-right">{f.users}</td>
                                    <td className="px-4 py-3 text-right">{f.clients}</td>
                                    <td className="px-4 py-3 text-right font-mono text-xs">
                                        {(f.storage_bytes / 1024 / 1024).toFixed(1)} MB
                                    </td>
                                    <td className="px-4 py-3 text-xs text-slate-500">
                                        <div>{fmtDate(f.created_at)}</div>
                                        <div>{fromNow(f.created_at)}</div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex justify-end gap-1">
                                            {f.status === "pending_approval" && (
                                                <>
                                                    <Button size="sm" onClick={() => approve(f)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`approve-${f.id}`}>
                                                        <CheckCircle2 size={13} className="mr-1" /> Approve
                                                    </Button>
                                                    <Button size="sm" variant="outline" onClick={() => { setRejectFirm(f); setReason(""); }} data-testid={`reject-${f.id}`}>
                                                        <XCircle size={13} className="mr-1" /> Reject
                                                    </Button>
                                                </>
                                            )}
                                            {f.status === "active" && (
                                                <Button size="sm" variant="outline" onClick={() => suspend(f)}>
                                                    <PauseCircle size={13} className="mr-1" /> Suspend
                                                </Button>
                                            )}
                                            {f.status === "suspended" && (
                                                <Button size="sm" variant="outline" onClick={() => activate(f)}>
                                                    <PlayCircle size={13} className="mr-1" /> Activate
                                                </Button>
                                            )}
                                            {f.status !== "rejected" && (
                                                <>
                                                    <Button size="sm" variant="ghost" onClick={() => { setPlanFirm(f); setPlanSel(f.plan); }}>
                                                        Plan
                                                    </Button>
                                                    <Button size="sm" variant="ghost" onClick={() => { setPwFirm(f); setNewPw(""); }} title="Reset Firm Admin password">
                                                        <KeyRound size={13} />
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {firms.length === 0 && (
                                <tr>
                                    <td colSpan={8} className="text-center py-12 text-slate-500">
                                        No firms match this filter.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>

            <Dialog open={!!rejectFirm} onOpenChange={(o) => !o && setRejectFirm(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-display">Reject firm registration</DialogTitle>
                    </DialogHeader>
                    <div className="text-sm text-slate-600">
                        {rejectFirm?.name} will be notified with your reason.
                    </div>
                    <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (shared with applicant)" data-testid="reject-reason" />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRejectFirm(null)}>Cancel</Button>
                        <Button onClick={doReject} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="confirm-reject">Send rejection</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={!!planFirm} onOpenChange={(o) => !o && setPlanFirm(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-display">Change plan – {planFirm?.name}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <Label className="field-label">Plan</Label>
                        <Select value={planSel} onValueChange={setPlanSel}>
                            <SelectTrigger data-testid="plan-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {Object.keys(plans).map((p) => (
                                    <SelectItem key={p} value={p}>
                                        {p} — {plans[p].users}u · {plans[p].clients}c · {plans[p].storage_mb}MB
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPlanFirm(null)}>Cancel</Button>
                        <Button onClick={savePlan} className="bg-primary hover:bg-primary/90" data-testid="save-plan">Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={!!pwFirm} onOpenChange={(o) => !o && setPwFirm(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-display">Reset Firm Admin password</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-slate-500">
                        You will not see the firm's data. Share this new password with the Firm Admin via a secure channel.
                    </p>
                    <Input type="text" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="New password (min 8 chars)" data-testid="new-password" />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPwFirm(null)}>Cancel</Button>
                        <Button onClick={doResetPw} className="bg-primary hover:bg-primary/90" data-testid="confirm-reset">Reset</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
